import { useCallback, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    createStyles,
    FormControlLabel,
    FormHelperText,
    ListItemText,
    MenuItem,
    Switch,
    Theme,
    WithStyles,
    withStyles
} from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";
import { Save } from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { matchSorter } from "match-sorter";
import { enqueueSnackbar } from "notistack";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router";
import { createPatch } from "rfc6902";
import * as z from "zod";
import { useGetUserListQuery } from "../../../../api/accountApiSlice";
import {
    useGetAuctionByUrlQuery,
    useUpdateAuctionMutation
} from "../../../../api/auctionApiSlice";
import IUserInfo from "../../../../models/account/IUserInfo";
import * as AuctionStore from "../../../../store/AuctionStore";
import { useAppDispatch } from "../../../../store/hooks";
import LoaderButton from "../../../shared/LoaderButton";

type IApprovedAuctionBiddersTab = WithStyles<typeof styles>;

const styles = (theme: Theme) =>
    createStyles({
        formSection: {
            marginBottom: theme.spacing(5),
            backgroundColor: theme.palette.common.black,
            padding: theme.spacing(4)
        },
        allowedBidderMenuItem: {
            backgroundColor: "transparent !important",
            margin: 0
        },
        allowedBidderSelect: {
            marginTop: theme.spacing(4)
        },
        saveButton: {
            marginTop: theme.spacing(4)
        }
    });

const schema = z.object({
    isApprovalRequired: z.boolean(),
    approvedBidders: z.array(z.string())
});

interface IApprovedAuctionBiddersFormValues extends z.infer<typeof schema> {}

function ApprovedAuctionBiddersTab({ classes }: IApprovedAuctionBiddersTab) {
    const dispatch = useAppDispatch();

    const { control, setError, setValue, reset } =
        useForm<IApprovedAuctionBiddersFormValues>({
            defaultValues: {
                approvedBidders: [],
                isApprovalRequired: false
            },
            resolver: zodResolver(schema)
        });

    const isApprovalRequiredFormValue = useWatch({
        name: "isApprovalRequired",
        control
    });

    const approvedBiddersFormValue = useWatch({
        name: "approvedBidders",
        control
    });

    const params = useParams<{ url?: string }>();
    const saleUrl = params.url ?? "";

    const auctionByUrlQuery = useGetAuctionByUrlQuery(saleUrl);
    const getUserListQuery = useGetUserListQuery(undefined, {});
    const [updateAuctionMutation, updateAuctionMutationData] =
        useUpdateAuctionMutation();

    if (!saleUrl) {
        throw new Error("`url` parameter is required");
    }

    const isLoading =
        auctionByUrlQuery.isLoading ||
        getUserListQuery.isLoading ||
        updateAuctionMutationData.isLoading;

    const isDisabled = auctionByUrlQuery.data?.isCompleted;

    useEffect(() => {
        if (auctionByUrlQuery.data) {
            reset({
                approvedBidders:
                    auctionByUrlQuery.data.approvedAuctionBidderUserIds,
                isApprovalRequired: auctionByUrlQuery.data.isApprovalRequired
            });
        }
    }, [auctionByUrlQuery.data, reset]);

    const resetFormErrors = () =>
        setError("isApprovalRequired", { message: "" });

    const onSubmit = async () => {
        try {
            resetFormErrors();

            if (!auctionByUrlQuery?.data?.auctionId) {
                throw new Error("Auction not found");
            }

            const patch = createPatch(
                {
                    isApprovalRequired:
                        auctionByUrlQuery?.data?.isApprovalRequired,
                    approvedAuctionBidderUserIds:
                        auctionByUrlQuery?.data?.approvedAuctionBidderUserIds
                },
                {
                    isApprovalRequired: isApprovalRequiredFormValue,
                    approvedAuctionBidderUserIds: approvedBiddersFormValue
                }
            );

            if (patch.length > 0) {
                await updateAuctionMutation({
                    auctionId: auctionByUrlQuery?.data?.auctionId || "",
                    patch
                }).unwrap();
            }

            // Reloads auction details for this tab
            auctionByUrlQuery.refetch();
            // Reload auction details for the "Auction Details" tab, so saving there will not overwrite changes made here
            dispatch(AuctionStore.actionCreators.loadAuctionDetail(saleUrl));

            enqueueSnackbar("Approved bidders successfully saved", {
                anchorOrigin: { horizontal: "left", vertical: "top" },
                preventDuplicate: false,
                variant: "success"
            });
        } catch (e) {
            console.error(e);

            enqueueSnackbar("Approved bidders failed to save", {
                anchorOrigin: { horizontal: "left", vertical: "top" },
                preventDuplicate: false,
                variant: "error"
            });

            if (e instanceof z.ZodError) {
                return setError("isApprovalRequired", {
                    message: e.errors[0].message
                });
            }
            if (e instanceof Error) {
                return setError("isApprovalRequired", {
                    message: (e as Error).message
                });
            }
            return setError("isApprovalRequired", {
                message: "Unknown error occurred while saving"
            });
        }
    };

    const formatFullName = (bidder: IUserInfo) =>
        `${bidder.firstName} ${bidder.lastName}`;

    const filterOptions = useCallback(
        (options: IUserInfo[], { inputValue }: { inputValue: string }) =>
            matchSorter(options, inputValue, {
                keys: ["firstName", "lastName", "email"]
            }),
        []
    );

    const potentialBidders = useMemo(
        () => getUserListQuery?.data?.bidders || [],
        [getUserListQuery?.data?.bidders]
    );
    const selectedBidders = useMemo(
        () =>
            potentialBidders.filter((b) =>
                approvedBiddersFormValue.includes(b.id)
            ),
        [potentialBidders, approvedBiddersFormValue]
    );

    return (
        <>
            <div className={classes.formSection} />
            <div className={classes.formSection}>
                <FormControlLabel
                    label="Approval Mode"
                    control={
                        <Controller
                            name="isApprovalRequired"
                            control={control}
                            render={({ field }) => (
                                <Switch
                                    {...field}
                                    disabled={isDisabled}
                                    checked={field.value}
                                    color="primary"
                                />
                            )}
                        />
                    }
                />
                <FormHelperText>
                    If approval mode is enabled, this sale will require bidders
                    to be approved.
                </FormHelperText>
                {isApprovalRequiredFormValue && (
                    <Autocomplete
                        multiple
                        options={potentialBidders}
                        className={classes.allowedBidderSelect}
                        disableCloseOnSelect
                        disabled={isDisabled}
                        autoComplete={false}
                        value={selectedBidders}
                        onChange={(_, newValue) => {
                            setValue(
                                "approvedBidders",
                                newValue.map((b) => b.id)
                            );
                        }}
                        getOptionLabel={(bidder) => formatFullName(bidder)}
                        renderOption={(bidder, { selected }) => (
                            <MenuItem
                                key={bidder.id}
                                value={bidder.id}
                                className={classes.allowedBidderMenuItem}
                            >
                                <Checkbox checked={selected} color="primary" />
                                <ListItemText
                                    primary={formatFullName(bidder)}
                                    secondary={bidder.email}
                                />
                            </MenuItem>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Approved Bidders"
                                placeholder="Search by name or email"
                            />
                        )}
                        filterOptions={filterOptions}
                    />
                )}

                <LoaderButton
                    className={classes.saveButton}
                    disabled={isDisabled}
                    loading={isLoading}
                    size="large"
                    variant="contained"
                    color="primary"
                    onClick={onSubmit}
                >
                    <Save />
                    &nbsp;Save
                </LoaderButton>
            </div>
        </>
    );
}

export default withStyles(styles)(ApprovedAuctionBiddersTab);
